import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router';

import { demanderReinitialisation } from '../api/motdepasse';
import { useSession } from '../app/SessionContext';

/** Site public de la marque : le lien « Retour au site » mène quelque part de réel. */
const SITE_PUBLIC = 'https://registre-securite-numerique.fr';

/**
 * Entrée dans l'espace prestataire.
 *
 * Le message d'erreur affiché est celui du SERVEUR : il distingue un mot de passe
 * court, une adresse déjà prise et des identifiants faux. On ne devine pas mieux
 * que lui, et on ne dit jamais « cet e-mail n'existe pas » (cela listerait les
 * comptes à un curieux).
 */
export function Connexion() {
  const { utilisateur, connecter } = useSession();
  const emplacement = useLocation();
  // La garde a mémorisé l'écran demandé : une fois la session ouverte, on y
  // retourne. Sans cette redirection, une inscription réussie laissait
  // l'utilisateur sur le formulaire.
  const versOu = (emplacement.state as { depuis?: string } | null)?.depuis ?? '/';
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [seSouvenir, setSeSouvenir] = useState(false);
  const [motDePasseVisible, setMotDePasseVisible] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Demande de réinitialisation : l'administrateur en est prévenu par le journal. */
  const [modeDemande, setModeDemande] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [envoiDemande, setEnvoiDemande] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);
    const message = await connecter(email, motDePasse, seSouvenir);
    setEnvoi(false);
    if (message) setErreur(message);
  };

  const demander = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setEnvoiDemande(true);
    setErreur(null);
    setMessage(null);

    const resultat = await demanderReinitialisation(email);
    setEnvoiDemande(false);

    if (resultat.ok) {
      // Le serveur répond toujours pareil : l'écran n'affirme donc pas qu'un
      // compte existe, il dit ce qui se passera si c'est le cas.
      setMessage(
        'Demande enregistrée. Si un compte correspond à cette adresse, un administrateur vous transmettra un lien.',
      );
    } else {
      setErreur(resultat.message ?? 'Demande impossible.');
    }
  };

  // Session déjà ouverte (retour arrière, ou inscription à l'instant) : on
  // quitte le formulaire sans rien redemander.
  if (utilisateur) return <Navigate to={versOu} replace />;

  const libelle = 'Accéder au registre';
  const champ =
    'w-full py-2.5 rounded-lg bg-[var(--app-surface-2)] border border-transparent text-sm text-slate-800 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white';
  const etiquette = 'block text-xs font-semibold text-slate-700 mb-1';

  return (
    <div className="relative min-h-screen bg-[var(--app-background)] px-4 py-10 flex flex-col items-center justify-center overflow-hidden">
      {/* Liseré de charte en haut de page. */}
      <div
        className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-orange-600 to-red-600"
        aria-hidden="true"
      />
      {/* Halos très diffus du modèle : beige à gauche, rosé à droite. */}
      <div
        className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-amber-200/50 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-rose-200/40 blur-3xl"
        aria-hidden="true"
      />

      <main className="relative w-full max-w-[420px]">
        <div className="overflow-hidden rounded-lg border border-slate-200/70 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.10)]">
          <div
            className="h-1 w-full bg-gradient-to-r from-amber-500 via-orange-600 to-red-600"
            aria-hidden="true"
          />

          <div className="px-7 py-6">
            <div className="mb-5 flex items-center justify-between">
              <a
                href={SITE_PUBLIC}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
              >
                <ArrowLeft className="h-3 w-3" aria-hidden="true" />
                Retour au site
              </a>
              <a
                href="/api/health"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700"
              >
                <Sparkles className="h-3 w-3 text-amber-500" aria-hidden="true" />
                API
              </a>
            </div>

            <div className="mb-6 text-center">
              <img
                src="/logo-registre.webp"
                alt="Registre-Sécurité — registre de sécurité incendie numérique"
                className="logo-marque mx-auto w-60 h-auto"
              />
              <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.3em] text-slate-500">
                Portail fournisseur
              </p>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-red-600">
                Connexion
              </p>
              <h1 className="mt-1 text-lg font-semibold text-slate-900">Bon retour</h1>
              <p className="mt-1 text-sm text-slate-500">
                Connectez-vous pour accéder à vos registres.
              </p>
            </div>

            {modeDemande && (
              <form
                onSubmit={(evenement) => void demander(evenement)}
                className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3"
                aria-labelledby="titre-demande"
              >
                <p id="titre-demande" className="text-xs text-slate-600 mb-2">
                  Indiquez votre adresse : un administrateur créera un lien de réinitialisation et
                  vous le transmettra. Aucun courriel automatique n’est envoyé.
                </p>
                <button
                  type="submit"
                  disabled={envoiDemande || email.trim() === ''}
                  className="w-full py-2 border border-slate-300 bg-white text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 disabled:opacity-60"
                >
                  {envoiDemande ? 'Envoi…' : 'Demander un lien de réinitialisation'}
                </button>
              </form>
            )}

            {message && (
              <p
                role="status"
                className="mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
              >
                {message}
              </p>
            )}

            <form onSubmit={(evenement) => void soumettre(evenement)} className="space-y-4">
              <div>
                <label htmlFor="email" className={etiquette}>
                  Email
                </label>
                <div className="relative">
                  <Mail
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(evenement) => setEmail(evenement.target.value)}
                    className={`${champ} pl-10 pr-3`}
                    autoComplete="email"
                    placeholder="vous@entreprise.fr"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="mot-de-passe" className={etiquette}>
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    id="mot-de-passe"
                    name="mot-de-passe"
                    type={motDePasseVisible ? 'text' : 'password'}
                    required
                    value={motDePasse}
                    onChange={(evenement) => setMotDePasse(evenement.target.value)}
                    className={`${champ} pl-10 pr-10`}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setMotDePasseVisible((visible) => !visible)}
                    aria-label={
                      motDePasseVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
                    }
                    aria-pressed={motDePasseVisible}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-white hover:text-slate-600"
                  >
                    {motDePasseVisible ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor="se-souvenir"
                  className="flex items-center gap-2 text-xs text-slate-600"
                >
                  <input
                    id="se-souvenir"
                    name="se-souvenir"
                    type="checkbox"
                    checked={seSouvenir}
                    onChange={(evenement) => setSeSouvenir(evenement.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                  />
                  Se souvenir de moi
                </label>

                <button
                  type="button"
                  onClick={() => {
                    setModeDemande((ouvert) => !ouvert);
                    setErreur(null);
                    setMessage(null);
                  }}
                  aria-expanded={modeDemande}
                  className="text-xs font-medium text-red-600 hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              {erreur && (
                <p
                  role="alert"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {erreur}
                </p>
              )}

              <button
                type="submit"
                disabled={envoi}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
              >
                {envoi ? 'Envoi…' : libelle}
                {!envoi && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
              </button>
            </form>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Données chiffrées • Hébergé en France
            </p>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          L’accès se fait <span className="font-medium text-slate-700">sur invitation</span> :
          demandez votre lien à un administrateur.
        </p>

        <p className="mt-2 text-center text-xs text-slate-500">
          Session portée par un cookie <code>HttpOnly</code> — aucun jeton dans le navigateur.
        </p>
      </main>
    </div>
  );
}
